import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { To, useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteInvoiceClientsMutation,
  useGetInvoiceClientQuery
} from '../../../../../services/endpoints/client/invoiceClient';
import { InvoiceClient } from '../../../../../types/finance.types';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';

export const InvoiceClientDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetInvoiceClientQuery(+params.id!);
  const [invoiceClient, setInvoiceClient] = useState<InvoiceClient>();
  const [deleteInvoiceClient, { isLoading: isDeleting }] = useDeleteInvoiceClientsMutation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading invoice client', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setInvoiceClient(data.invoiceClient);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteInvoiceClient({ ids: [+params.id!] }).unwrap();
      addToast('Invoice client deleted successfully', 'success');
      navigate('/admin/invoice-clients');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !invoiceClient) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(-1 as To)}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Invoice Clients List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Invoice Client #${invoiceClient?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/invoice-clients/${invoiceClient?.id}/edit`)}
          >
            Edit Invoice Client
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            Delete Invoice Client
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Name:"
              text={invoiceClient?.name}
            />
            <LabelText
              label="Created At:"
              text={
                invoiceClient?.created_at
                  ? dateToUTC(new Date(invoiceClient?.created_at), 'MM/dd/yyyy')
                  : 'N/A'
              }
            />
            <LabelText
              label="Invoiceable Type:"
              text={invoiceClient?.invoiceable_type}
            />
            {invoiceClient?.invoiceable_type === 'Venue' && (
              <LabelText label="Invoiceable:">
                <LinkCell
                  content={invoiceClient?.venue?.title ?? ''}
                  url={`/admin/places/venues/${invoiceClient?.venue?.id}`}
                />
              </LabelText>
            )}
            {invoiceClient?.invoiceable_type === 'Organization' && (
              <LabelText label="Invoiceable:">
                <LinkCell
                  content={invoiceClient?.organization?.name ?? ''}
                  url={`/admin/places/organizations/${invoiceClient?.invoiceable_id}`}
                />
              </LabelText>
            )}
            {invoiceClient?.invoiceable_type === 'Contact' && (
              <LabelText label="Invoiceable:">
                {/* <LinkCell content={'EMPTY'} url={`/admin/contacts/${invoiceClient?.invoiceable_id}`} disabled={true} /> */}
                <p>EMPTY</p>
              </LabelText>
            )}
            {/* <LabelText label="Is Personal:" >
              <YesNoCell value={invoiceClient?.is_personal} />  
            </LabelText> */}
          </DetailSection>
        </Stack>
      </div>
      <ConfirmationModal
        show={showModal}
        title="Delete Invoice Client"
        message="Are you sure you want to delete this invoice client?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
